/** The page's floating pieces: a menu by the button that opened it, and the full-screen viewer. */
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { isStreamUrl, useNoteStream } from "../../lib/noteStream";
import { usePage } from "./context";
import { IcDl, IcX } from "./icons";

export function Menu(): ReactNode {
  const { menu, openMenu } = usePage();
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number; oy: string } | null>(null);
  useLayoutEffect(() => {
    const m = ref.current;
    if (!menu || !m) {
      setPlace(null);
      return;
    }
    const r = menu.anchor;
    const mw = m.offsetWidth;
    const mh = m.offsetHeight;
    let left = Math.min(Math.max(12, r.left + r.width / 2 - mw / 2), window.innerWidth - mw - 12);
    if (menu.attach) left = Math.max(12, r.left - 4);
    let top = r.top - mh - 8;
    let oy = "100%";
    if (top < 12) {
      top = r.bottom + 8;
      oy = "0%";
    }
    setPlace({ left, top, oy });
  }, [menu]);
  useEffect(() => {
    if (!menu) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      if (e.type === "pointerdown" && ref.current?.contains(e.target as Node)) return;
      if (e.type === "pointerdown" && (e.target as Element).closest?.("[data-menu-anchor]")) return;
      openMenu(null);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("resize", close);
    };
  }, [menu, openMenu]);
  if (!menu) return null;
  return (
    <div
      ref={ref}
      className={`menu${menu.attach ? " attmenu" : ""}`}
      role="menu"
      style={{
        left: place?.left ?? -9999,
        top: place?.top ?? -9999,
        ...(place ? ({ "--oy": place.oy, ...(menu.attach ? { "--ox": "0%" } : {}) } as React.CSSProperties) : {}),
      }}
    >
      {menu.items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="menuitem"
          onClick={() => {
            openMenu(null);
            it.run();
          }}
        >
          {it.icon}
          <span>
            <b>{it.label}</b>
            {it.sub ? <small>{it.sub}</small> : null}
          </span>
        </button>
      ))}
    </div>
  );
}

/** A video opened full screen: one file plays as itself, a long one as its stream. */
function ViewerVideo({ url }: { url: string }): ReactNode {
  const ref = useRef<HTMLVideoElement>(null);
  const stream = useNoteStream(ref, url);
  return <video ref={ref} src={stream ? undefined : url} controls autoPlay playsInline />;
}

export function Viewer(): ReactNode {
  const { viewing, view } = usePage();
  useEffect(() => {
    if (!viewing) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") view(null);
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [viewing, view]);
  if (!viewing) return null;
  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={viewing.name}>
      <div className="vw-bar">
        <span className="vw-n">
          <b>{viewing.name}</b>
          <small>{viewing.meta}</small>
        </span>
        {/* A long video plays as a stream, which is no one file to save. */}
        {isStreamUrl(viewing.url) ? null : (
          <a className="vw-b" href={viewing.url} download={viewing.name}>
            <IcDl s={17} />
            Save
          </a>
        )}
        <button type="button" className="vw-b vw-x" aria-label="Close" onClick={() => view(null)}>
          <IcX s={16} />
        </button>
      </div>
      <div
        className="vw-stage"
        onClick={(e) => {
          if (!(e.target as Element).closest("img,video")) view(null);
        }}
      >
        {viewing.video ? <ViewerVideo url={viewing.url} /> : <img src={viewing.url} alt={viewing.name} />}
      </div>
    </div>
  );
}
